import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaHome, FaBox, FaCamera, FaClipboardList, FaUser, FaBolt, FaMobileAlt, FaChartLine, FaSync, FaUserTie } from 'react-icons/fa';
import theme from '../../theme';
import logoFooter from '../../assets/images/logo-footer.svg';

const SolutionsSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      direction: ltr;
    }
  }
  
  &:nth-child(even) > div {
    direction: ltr;
  }
`;

const ContentText = styled.div`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      line-height: 1.7;
    }
  }
`;

const ContentImage = styled.div`
  img {
    width: 100%;
    height: auto;
    border-radius: ${theme.borderRadius};
    box-shadow: ${theme.boxShadow};
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.boxShadow};
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1rem;
`;

const scanAnimation = keyframes`
  0% { top: 10%; opacity: 0; }
  50% { opacity: 1; }
  100% { top: 90%; opacity: 0; }
`;

const successPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const buttonClick = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.95); }
  100% { transform: scale(1); }
`;

const BarcodeScannerSection = styled.section`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 80px 5%;
  margin-bottom: 5rem;
  border-radius: 20px;
  overflow: hidden;
`;

const BarcodeContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 968px) { grid-template-columns: 1fr; gap: 3rem; }
`;

const BarcodeTextContent = styled.div`color: white;`;

const BarcodeTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const BarcodeTitle = styled.h2`
  font-size: 2.5rem; font-weight: 800; margin-bottom: 1.5rem; line-height: 1.2;
  @media (max-width: 768px) { font-size: 2rem; }
`;

const BarcodeDescription = styled.p`
  font-size: 1.1rem; line-height: 1.7; margin-bottom: 2rem; opacity: 0.95;
`;

const BarcodeFeatures = styled.ul`list-style: none; padding: 0; margin-bottom: 2rem;`;

const BarcodeFeature = styled.li`
  padding: 0.75rem 0; font-size: 1.05rem; display: flex; align-items: center;
  &:before {
    content: '✓'; display: inline-block; width: 24px; height: 24px;
    background: rgba(255, 255, 255, 0.3); border-radius: 50%;
    text-align: center; line-height: 24px; margin-right: 1rem; font-weight: bold;
  }
`;

const BarcodeHighlight = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 1.5rem; border-radius: 15px;
  border-left: 4px solid white; font-weight: 600; font-size: 1.1rem;
`;

const BarcodeVisualContent = styled.div`
  display: flex; justify-content: center; align-items: center;
`;

const PhoneMockup = styled.div`
  width: 320px; height: 640px; background: #1a1a1a;
  border-radius: 40px; padding: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4); position: relative;
  &:before {
    content: ''; position: absolute; top: 20px; left: 50%;
    transform: translateX(-50%); width: 60px; height: 6px;
    background: #333; border-radius: 3px;
  }
`;

const PhoneScreen = styled.div`
  width: 100%; height: 100%; background: #f5f5f5;
  border-radius: 32px; overflow: hidden; position: relative;
  display: flex; flex-direction: column;
`;

const AppHeader = styled.div`
  background: #dc2626; padding: 1rem; padding-top: 2.5rem;
  display: flex; align-items: center; justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); position: relative; z-index: 10;
`;

const AppLogo = styled.div`
  display: flex; align-items: center; height: 30px;
  img { height: 100%; width: auto; object-fit: contain; }
`;

const AppHeaderIcons = styled.div`
  display: flex; gap: 0.8rem; color: white; font-size: 1.2rem;
  svg { cursor: pointer; transition: transform 0.2s ease; &:hover { transform: scale(1.1); } }
`;

const AppContent = styled.div`
  flex: 1; overflow: hidden; position: relative; background: #f5f5f5;
`;

const BottomNav = styled.div`
  background: white; display: flex; justify-content: space-around;
  padding: 0.8rem 0; border-top: 1px solid #e0e0e0;
  position: relative; z-index: 10;
`;

const PhoneNavItem = styled.div`
  display: flex; flex-direction: column; align-items: center;
  font-size: 0.7rem; color: ${props => props.active ? theme.colors.primary : '#999'}; gap: 0.2rem;
  svg { font-size: 1.3rem; }
`;

const ScannerArea = styled.div`
  position: absolute; top: 30%; left: 50%; transform: translateX(-50%);
  width: 80%; height: 200px; background: rgba(102, 126, 234, 0.1);
  border-radius: 20px; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  border: 3px dashed ${theme.colors.primary};
`;

const ScannerFrame = styled.div`
  width: 200px; height: 150px; position: relative; margin-bottom: 1rem;
`;

const ScannerCorner = styled.div`
  position: absolute; width: 30px; height: 30px;
  border: 3px solid ${theme.colors.primary};
  ${props => props.position === 'top-left' && `top: 0; left: 0; border-right: none; border-bottom: none;`}
  ${props => props.position === 'top-right' && `top: 0; right: 0; border-left: none; border-bottom: none;`}
  ${props => props.position === 'bottom-left' && `bottom: 0; left: 0; border-right: none; border-top: none;`}
  ${props => props.position === 'bottom-right' && `bottom: 0; right: 0; border-left: none; border-top: none;`}
`;

const ScanLine = styled.div`
  position: absolute; width: 100%; height: 3px;
  background: linear-gradient(90deg, transparent, ${theme.colors.primary}, transparent);
  animation: ${scanAnimation} 2s ease-in-out infinite;
  box-shadow: 0 0 10px ${theme.colors.primary};
`;

const BarcodeIcon = styled.div`font-size: 3rem; margin-bottom: 0.5rem;`;

const ScannerText = styled.div`
  color: ${theme.colors.primary}; font-weight: 600; font-size: 0.9rem;
`;

const BarcodeExample = styled.div`
  position: absolute; bottom: 20%; left: 50%; transform: translateX(-50%);
  background: white; padding: 1rem; border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.show ? 1 : 0}; transition: opacity 0.3s ease;
`;

const BarcodeStripes = styled.div`
  display: flex; gap: 2px; padding: 0.5rem; background: white; border-radius: 5px;
`;

const BarcodeStripe = styled.div`
  width: ${props => props.width || '3px'}; height: 60px; background: #000;
`;

const BarcodeNumber = styled.div`
  text-align: center; font-size: 0.85rem; font-weight: 600;
  margin-top: 0.5rem; letter-spacing: 2px;
`;

const ProductCard = styled.div`
  position: absolute; top: 10%; left: 50%; transform: translateX(-50%);
  width: 85%; background: white; border-radius: 15px; padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.show ? 1 : 0};
  animation: ${props => props.show ? fadeIn : 'none'} 0.5s ease;
  transition: opacity 0.3s ease;
`;

const ProductImage = styled.div`
  width: 100%; height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px; display: flex; align-items: center;
  justify-content: center; font-size: 3rem; margin-bottom: 1rem;
`;

const ProductName = styled.h4`
  font-size: 1.1rem; font-weight: 700; color: #1a1a1a; margin-bottom: 0.5rem;
`;

const ProductCode = styled.p`font-size: 0.85rem; color: #666; margin-bottom: 0.5rem;`;

const ProductPrice = styled.div`
  font-size: 1.3rem; font-weight: 800; color: ${theme.colors.primary}; margin-bottom: 1rem;
`;

const StockStatus = styled.div`
  display: inline-block; background: #10b981; color: white;
  padding: 0.3rem 0.8rem; border-radius: 20px;
  font-size: 0.8rem; font-weight: 600; margin-bottom: 1rem;
`;

const AddToCartButton = styled.button`
  width: 100%; background: ${theme.colors.primary}; color: white;
  border: none; padding: 1rem; border-radius: 10px;
  font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: all 0.3s ease;
  animation: ${props => props.clicked ? buttonClick : 'none'} 0.3s ease;
  &:hover { background: ${theme.colors.accent}; transform: translateY(-2px); box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3); }
  &:active { transform: scale(0.95); }
`;

const SuccessMessage = styled.div`
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  background: #10b981; color: white; padding: 1.5rem 2rem;
  border-radius: 15px; font-weight: 700; font-size: 1.1rem;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  animation: ${successPulse} 1s ease-in-out;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center; gap: 0.5rem;
  &:before { content: '✓'; font-size: 1.5rem; }
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary}; color: ${theme.colors.white};
  padding: 4rem 0; text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge}; margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px; margin: 0 auto 2rem; font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block; background-color: ${theme.colors.primary};
  color: ${theme.colors.white}; padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium}; font-weight: 600;
  border-radius: ${theme.borderRadius}; transition: ${theme.transition};
  &:hover { background-color: ${theme.colors.accent}; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1); }
`;

const ECommerceSolutionsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [scanStep, setScanStep] = useState(1);
  const [buttonClicked, setButtonClicked] = useState(false);
  
  useEffect(() => {
    const timer1 = setTimeout(() => { setScanStep(2); }, 2500);
    const timer2 = setTimeout(() => {
      setButtonClicked(true);
      setTimeout(() => { setScanStep(3); setButtonClicked(false); }, 300);
    }, 5000);
    const timer3 = setTimeout(() => { setScanStep(1); }, 7500);
    return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
  }, [scanStep]);
  
  const features = [
    { icon: <FaBolt />, titleKey: 'ecommerce.features.quickOrder.title', descKey: 'ecommerce.features.quickOrder.description' },
    { icon: <FaMobileAlt />, titleKey: 'ecommerce.features.mobile.title', descKey: 'ecommerce.features.mobile.description' },
    { icon: <FaUserTie />, titleKey: 'ecommerce.highlights.salesRep.title', descKey: 'ecommerce.highlights.salesRep.description' },
    { icon: <FaChartLine />, titleKey: 'ecommerce.features.reporting.title', descKey: 'ecommerce.features.reporting.description' },
    { icon: <FaSync />, titleKey: 'ecommerce.features.erp.title', descKey: 'ecommerce.features.erp.description' },
    { icon: <FaBox />, titleKey: 'ecommerce.features.stock.title', descKey: 'ecommerce.features.stock.description' },
  ];
  
  return (
    <>
      <SEO titleKey="ecommerceSolutionsPage.seo.title" descriptionKey="ecommerceSolutionsPage.seo.description" />
      
      <SolutionsSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('ecommerce.pageTitle')}</PageTitle>
            <PageSubtitle>{t('ecommerce.pageSubtitle')}</PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('ecommerce.b2bSection.title')}</SectionTitle>
                <p>{t('ecommerce.b2bSection.paragraph1')}</p>
                <p>{t('ecommerce.b2bSection.paragraph2')}</p>
                <ul>
                  <li>{t('ecommerce.b2bSection.bullet1')}</li>
                  <li>{t('ecommerce.b2bSection.bullet2')}</li>
                  <li>{t('ecommerce.b2bSection.bullet3')}</li>
                  <li>{t('ecommerce.b2bSection.bullet4')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/ecom-1.jpeg`} alt={t('ecommerce.b2bSection.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('ecommerce.erpSection.title')}</SectionTitle>
                <p>{t('ecommerce.erpSection.paragraph1')}</p>
                <p>{t('ecommerce.erpSection.paragraph2')}</p>
                <ul>
                  <li>{t('ecommerce.erpSection.bullet1')}</li>
                  <li>{t('ecommerce.erpSection.bullet2')}</li>
                  <li>{t('ecommerce.erpSection.bullet3')}</li>
                  <li>{t('ecommerce.erpSection.bullet4')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/ecom-2.jpeg`} alt={t('ecommerce.erpSection.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <BarcodeScannerSection>
              <BarcodeContent>
                <BarcodeTextContent>
                  <BarcodeTag>{t('ecommerce.barcodeScanner.tag')}</BarcodeTag>
                  <BarcodeTitle>{t('ecommerce.barcodeScanner.title')}</BarcodeTitle>
                  <BarcodeDescription>{t('ecommerce.barcodeScanner.description')}</BarcodeDescription>
                  <BarcodeFeatures>
                    <BarcodeFeature>{t('ecommerce.barcodeScanner.feature1')}</BarcodeFeature>
                    <BarcodeFeature>{t('ecommerce.barcodeScanner.feature2')}</BarcodeFeature>
                    <BarcodeFeature>{t('ecommerce.barcodeScanner.feature3')}</BarcodeFeature>
                    <BarcodeFeature>{t('ecommerce.barcodeScanner.feature4')}</BarcodeFeature>
                  </BarcodeFeatures>
                  <BarcodeHighlight>{t('ecommerce.barcodeScanner.highlight')}</BarcodeHighlight>
                </BarcodeTextContent>

                <BarcodeVisualContent>
                  <PhoneMockup>
                    <PhoneScreen>
                      <AppHeader>
                        <AppLogo><img src={logoFooter} alt="PetekB2B" loading="lazy" /></AppLogo>
                        <AppHeaderIcons><FaSearch /></AppHeaderIcons>
                      </AppHeader>
                      
                      <AppContent>
                        {scanStep === 1 && (
                          <>
                            <ScannerArea>
                              <ScannerFrame>
                                <ScannerCorner position="top-left" />
                                <ScannerCorner position="top-right" />
                                <ScannerCorner position="bottom-left" />
                                <ScannerCorner position="bottom-right" />
                                <ScanLine />
                              </ScannerFrame>
                              <BarcodeIcon>📷</BarcodeIcon>
                              <ScannerText>{t('ecommerce.barcodeScanner.scanText')}</ScannerText>
                            </ScannerArea>
                            <BarcodeExample show={true}>
                              <BarcodeStripes>
                                {[...Array(20)].map((_, i) => (
                                  <BarcodeStripe key={i} width={Math.random() > 0.5 ? '3px' : '6px'} />
                                ))}
                              </BarcodeStripes>
                              <BarcodeNumber>8 690504 123456</BarcodeNumber>
                            </BarcodeExample>
                          </>
                        )}
                        
                        {scanStep === 2 && (
                          <ProductCard show={true}>
                            <ProductImage>🧀</ProductImage>
                            <ProductName>{t('ecommerce.barcodeScanner.productName')}</ProductName>
                            <ProductCode>{t('ecommerce.barcodeScanner.productCode')}</ProductCode>
                            <StockStatus>{t('ecommerce.barcodeScanner.stockStatus')}</StockStatus>
                            <ProductPrice>{t('ecommerce.barcodeScanner.productPrice')}</ProductPrice>
                            <AddToCartButton clicked={buttonClicked}>
                              {t('ecommerce.barcodeScanner.addToCart')}
                            </AddToCartButton>
                          </ProductCard>
                        )}
                        
                        <SuccessMessage show={scanStep === 3}>
                          {t('ecommerce.barcodeScanner.success')}
                        </SuccessMessage>
                      </AppContent>
                      
                      <BottomNav>
                        <PhoneNavItem><FaHome /><span>{t('ecommerce.phoneNav.home')}</span></PhoneNavItem>
                        <PhoneNavItem><FaBox /><span>{t('ecommerce.phoneNav.products')}</span></PhoneNavItem>
                        <PhoneNavItem active><FaCamera /><span>{t('ecommerce.phoneNav.scan')}</span></PhoneNavItem>
                        <PhoneNavItem><FaClipboardList /><span>{t('ecommerce.phoneNav.orders')}</span></PhoneNavItem>
                        <PhoneNavItem><FaUser /><span>{t('ecommerce.phoneNav.profile')}</span></PhoneNavItem>
                      </BottomNav>
                    </PhoneScreen>
                  </PhoneMockup>
                </BarcodeVisualContent>
              </BarcodeContent>
            </BarcodeScannerSection>
            
            <SectionTitle>{t('ecommerce.features.title')}</SectionTitle>
            <p>{t('ecommerce.features.subtitle')}</p>
            
            <FeatureGrid>
              {features.map((feature, index) => (
                <FeatureCard key={index}>
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                  <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
                  <p>{t(feature.descKey)}</p>
                </FeatureCard>
              ))}
            </FeatureGrid>
          </ContentSection>
        </div>
      </SolutionsSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('ecommerce.cta.title')}</CTATitle>
          <CTAText>{t('ecommerce.cta.text')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t('ecommerce.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default ECommerceSolutionsPage;
